import React, { useMemo, useState, useEffect } from 'react';
import { localized } from '../i18n/translations';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import NotifyAvailable from "../components/NotifyAvailable";
import CustomImage from '../components/CustomImage';
import SEO from '../components/SEO';

function Category({ cartItems, productsData, ChangeQty, addToCart, currency, country, translate, categories }) {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [notifyProductId, setNotifyProductId] = useState(null);
  const [sortBy, setSortBy] = useState('default');

  const closeModal = () => {
    setShowModal(false);
  };

  const category = categories.find(cat => cat.path.toLowerCase() === categoryName.toLowerCase());
  const categoryId = category?.id;
  const productsInCategory = categoryId ? productsData.filter(product => product.category === categoryId) : [];

  // Redirect to product page if category has only one product
  useEffect(() => {
    if (category && productsInCategory.length === 1) {
      const singleProduct = productsInCategory[0];
      navigate(`/proizvod/${category.path}/${singleProduct.path}`, { replace: true });
    }
  }, [category, productsInCategory, navigate]);

  // Sort products based on sortBy
  const sortedProducts = useMemo(() => {
    if (!productsInCategory || productsInCategory.length === 0) return [];
    
    let sorted = [...productsInCategory];
    
    if (sortBy === 'price-low') {
      return sorted.sort((a, b) => {
        const priceA = a.is_on_sale && a.sale_discount_percentage > 0 
          ? a[`discounted_${currency}`] 
          : a[currency];
        const priceB = b.is_on_sale && b.sale_discount_percentage > 0 
          ? b[`discounted_${currency}`] 
          : b[currency];
        return priceA - priceB;
      });
    } else if (sortBy === 'price-high') {
      return sorted.sort((a, b) => {
        const priceA = a.is_on_sale && a.sale_discount_percentage > 0 
          ? a[`discounted_${currency}`] 
          : a[currency];
        const priceB = b.is_on_sale && b.sale_discount_percentage > 0 
          ? b[`discounted_${currency}`] 
          : b[currency];
        return priceB - priceA;
      });
    } else if (sortBy === 'date-new') {
      return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'date-old') {
      return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    
    return sorted;
  }, [productsInCategory, sortBy, currency]);

  const productsItems = sortedProducts.map(product => {
    return (
      <div className="group" key={product.id}>
        <Link to={`/proizvod/${category?.path || 'default'}/${product.path}`} className="block">
          <div className="relative bg-white">
            {/* Sale Badge */}
            {product.is_on_sale && product.sale_discount_percentage > 0 && (
              <div className="absolute top-2 right-2 z-10">
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                  -{product.sale_discount_percentage}%
                </span>
              </div>
            )}
            {/* Rasprodato (Sold Out) Badge */}
            {((product.stock_tracking_enabled && product.stock_quantity === 0) || !product.is_in_stock) && (
              <div className={`absolute ${product.is_on_sale && product.sale_discount_percentage > 0 ? 'top-10' : 'top-2'} right-2 z-10`}>
                <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  {translate("Sold out")}
                </span>
              </div>
            )}
            <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden">
              <CustomImage 
                src={product.imageUrl} 
                alt={product.imageAlt} 
                className="h-full w-full object-cover rounded-xl transition-transform duration-300 group-hover:scale-105" 
              />
            </div>
            <div className="p-2">
              <div>
                <Link to={`/proizvod/${category?.path || 'default'}/${product.path}`} className="no-underline text-brownSkin inline-block">
                  <h3 className="text-base font-medium leading-tight mb-1 text-brownSkin hover:text-brownSkin/80 transition-colors duration-200">
                    {localized(product, 'name', country)}
                  </h3>
                </Link>
                <div className="product-card__price-badge">
                  <div className="product-price">
                    {product.is_on_sale && product.sale_discount_percentage > 0 ? (
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-brownSkin font-medium">
                            {Number(product[`discounted_${currency}`] || 0).toFixed(2)} {currency}
                          </span>
                          <span className="text-sm text-gray-500 line-through">
                            {Number(product[currency] || 0).toFixed(2)} {currency}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <span className="text-brownSkin font-medium">
                          {Number(product[currency] || 0).toFixed(2)} {currency}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  });

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  if (!category) {
    return (
      <div className="bg-white min-h-screen">
        <div className="text-center py-16">
          <div className="text-6xl mb-4">🏺</div>
          <h3 className="text-2xl font-bold text-brownSkin mb-2">{translate("Category not found")}</h3>
          <p className="text-gray-600 mb-8">{translate('Category "{name}" does not exist.').replace('{name}', categoryName)}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-darkerSkin text-brownSkin px-8 py-3 rounded-full font-medium hover:bg-brownSkin hover:text-white transition-all duration-300"
          >
            {translate("Back to home")}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
            </svg>
          </Link>
        </div>
        <Footer categories={categories} />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title={`Bulka Ceramics - ${category.titleInCategory}`}
        description={category.descriptionInCategory}
        type="website"
        name="Bulka Ceramics"
      />

      {showModal && <NotifyAvailable onClose={closeModal} notifyProductId={notifyProductId} />}

      {/* Sorting Controls */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-brownSkin font-medium">
            {translate("Showing {count} products").replace('{count}', sortedProducts.length)}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="SortBy" className="text-brownSkin font-medium text-sm">{translate("Sort by:")}</label>
            </div>

            {/* Sort Select */}
            <div className="relative">
              <select
                name="sort_by"
                id="SortBy"
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="appearance-none bg-white border border-brownSkin/30 rounded-md px-4 py-2 pr-10 focus:ring-brownSkin focus:border-brownSkin focus:ring-1 text-sm min-w-[200px] text-brownSkin"
              >
                <option value="default">{translate("Featured")}</option>
                <option value="price-low">{translate("Price, low to high")}</option>
                <option value="price-high">{translate("Price, high to low")}</option>
                <option value="date-old">{translate("Date, oldest to newest")}</option>
                <option value="date-new">{translate("Date, newest to oldest")}</option>
              </select>
              <svg className="w-5 h-5 text-brownSkin absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Products Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
            {category.titleInCategory}
          </h2>
          <div className="w-24 h-1 bg-brownSkin rounded-full mx-auto mb-6"></div>
        </div>
        
        {sortedProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
            {productsItems}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🏺</div>
            <h3 className="text-2xl font-bold text-brownSkin mb-2">{translate("No products in this category")}</h3>
            <p className="text-gray-600 mb-8">{translate("No products currently available to display.")}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-darkerSkin text-brownSkin px-8 py-3 rounded-full font-medium hover:bg-brownSkin hover:text-white transition-all duration-300"
            >
              {translate("Back to home")}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
            </Link>
          </div>
        )}
      </div>

      <Footer categories={categories} />
    </div>
  );
}

export default Category;
